import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { DownloadSimple, MagnifyingGlass, CheckCircle, Warning, Link as LinkIcon } from "@phosphor-icons/react";
import {
  previewNeweggUrl,
  importNeweggUrl,
  bulkImportNewegg,
} from "../lib/api";
import { formatApiError } from "../lib/auth";
import { toast } from "sonner";

export const NeweggImportDialog = ({ open, onOpenChange, onSuccess }) => {
  const [url, setUrl] = useState("");
  const [bulkUrl, setBulkUrl] = useState("");
  const [bulkLimit, setBulkLimit] = useState(10);
  const [preview, setPreview] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setUrl("");
    setBulkUrl("");
    setPreview(null);
    setBulkResult(null);
    setLoading(false);
  };

  const handlePreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const data = await previewNeweggUrl(url);
      setPreview(data);
      toast.success("Product loaded");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    try {
      const data = await importNeweggUrl(url);
      toast.success(`Imported: ${data.name}`);
      reset();
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async () => {
    setLoading(true);
    setBulkResult(null);
    try {
      const data = await bulkImportNewegg(bulkUrl, parseInt(bulkLimit) || 10);
      setBulkResult(data);
      if (data.imported_count > 0) {
        toast.success(`Imported ${data.imported_count} products`);
        onSuccess?.();
      } else {
        toast.warning("No new products imported");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Bulk import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="rounded-none border-2 border-zinc-950 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="newegg-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-2xl tracking-tight flex items-center gap-2">
            <DownloadSimple size={24} weight="duotone" />
            IMPORT FROM NEWEGG
          </DialogTitle>
          <DialogDescription className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-500">
            Paste a Newegg product URL or category page
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="single" className="w-full">
          <TabsList className="rounded-none bg-zinc-100 p-1 mb-4">
            <TabsTrigger
              value="single"
              className="rounded-none font-mono uppercase tracking-wider"
              data-testid="tab-single-import"
            >
              <LinkIcon size={14} weight="bold" className="mr-2" /> Single product
            </TabsTrigger>
            <TabsTrigger
              value="bulk"
              className="rounded-none font-mono uppercase tracking-wider"
              data-testid="tab-bulk-import"
            >
              <DownloadSimple size={14} weight="bold" className="mr-2" /> Bulk (category)
            </TabsTrigger>
          </TabsList>

          {/* SINGLE */}
          <TabsContent value="single" className="space-y-4">
            <div>
              <label className="font-mono uppercase text-xs tracking-[0.2em] block mb-2">
                Newegg product URL
              </label>
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.newegg.com/p/N82E..."
                  className="rounded-none font-mono text-sm flex-1"
                  data-testid="newegg-single-url-input"
                />
                <Button
                  type="button"
                  onClick={handlePreview}
                  disabled={loading || !url.includes("newegg.com")}
                  data-testid="newegg-preview-button"
                  variant="outline"
                  className="rounded-none border-2 border-zinc-950 font-bold uppercase tracking-wider"
                >
                  <MagnifyingGlass size={16} weight="bold" />
                </Button>
              </div>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400 mt-1">
                Example: https://www.newegg.com/p/N82E16814932757
              </p>
            </div>

            {preview && (
              <div className="border-2 border-green-500 bg-green-50 p-4" data-testid="newegg-preview">
                <div className="flex gap-3">
                  <img
                    src={preview.image}
                    alt={preview.name}
                    className="w-24 h-24 object-cover border border-zinc-200 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-green-700 mb-1">
                      <CheckCircle size={10} weight="fill" className="inline mr-1" /> Ready to import
                    </div>
                    <h4 className="font-heading font-bold text-base leading-snug line-clamp-2">
                      {preview.name}
                    </h4>
                    <div className="font-mono text-xs text-zinc-600 mt-1">
                      {preview.brand} · {preview.category} · SKU {preview.sku}
                    </div>
                    <div className="font-mono text-2xl font-bold mt-2">
                      ${preview.price.toFixed(2)}
                      {preview.original_price && (
                        <span className="text-sm text-zinc-400 line-through ml-2">
                          ${preview.original_price.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-none"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={loading || !url.includes("newegg.com")}
                data-testid="newegg-import-button"
                className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider"
              >
                {loading ? "Importing..." : preview ? "Import this product" : "Import"}
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* BULK */}
          <TabsContent value="bulk" className="space-y-4">
            <div>
              <label className="font-mono uppercase text-xs tracking-[0.2em] block mb-2">
                Newegg category or search URL
              </label>
              <Input
                type="url"
                value={bulkUrl}
                onChange={(e) => setBulkUrl(e.target.value)}
                placeholder="https://www.newegg.com/Video-Cards-Video-Devices/Category/ID-38"
                className="rounded-none font-mono text-sm"
                data-testid="newegg-bulk-url-input"
              />
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400 mt-1">
                Examples: category pages, search results, deal pages
              </p>
            </div>

            <div>
              <label className="font-mono uppercase text-xs tracking-[0.2em] block mb-2">
                Max products to import (1–20)
              </label>
              <Input
                type="number"
                min="1"
                max="20"
                value={bulkLimit}
                onChange={(e) => setBulkLimit(e.target.value)}
                className="rounded-none font-mono text-sm w-32"
                data-testid="newegg-bulk-limit-input"
              />
            </div>

            <div className="bg-amber-50 border border-amber-300 p-3 text-xs font-mono text-amber-900">
              <Warning size={14} weight="fill" className="inline mr-1" />
              Bulk import takes ~1 second per product. Duplicates are skipped automatically.
            </div>

            {bulkResult && (
              <div className="border-2 border-zinc-200 p-4 space-y-3" data-testid="bulk-result">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="font-mono text-3xl font-bold text-green-600">
                      {bulkResult.imported_count}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      Imported
                    </div>
                  </div>
                  <div>
                    <div className="font-mono text-3xl font-bold text-amber-500">
                      {bulkResult.skipped_count}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      Skipped
                    </div>
                  </div>
                  <div>
                    <div className="font-mono text-3xl font-bold text-red-500">
                      {bulkResult.error_count}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      Errors
                    </div>
                  </div>
                </div>
                {bulkResult.imported.length > 0 && (
                  <details className="text-xs font-mono">
                    <summary className="cursor-pointer uppercase tracking-wider text-zinc-700 hover:text-zinc-950">
                      View imported ({bulkResult.imported.length})
                    </summary>
                    <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                      {bulkResult.imported.map((p) => (
                        <li key={p.id} className="border-b border-zinc-100 py-1">
                          ${p.price.toFixed(2)} — {p.name.slice(0, 80)}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-none"
                disabled={loading}
              >
                {bulkResult ? "Done" : "Cancel"}
              </Button>
              <Button
                onClick={handleBulkImport}
                disabled={loading || !bulkUrl.includes("newegg.com")}
                data-testid="newegg-bulk-import-button"
                className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider"
              >
                {loading ? "Importing..." : `Import up to ${bulkLimit}`}
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
